import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface PaymentMethod {
  id: string;
  type: 'card';
  lastFour: string;
  expiryMonth: string;
  expiryYear: string;
  brand: string;
  isDefault: boolean;
}

export interface Invoice {
  id: string;
  userId: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  date: string;
  description: string;
  paymentMethodId: string;
  receiptUrl?: string;
  type: 'subscription' | 'service_fee';
  leaseRequestId?: string;
}

export interface Subscription {
  id: string;
  userId: string;
  status: 'active' | 'canceled' | 'past_due';
  plan: 'premium';
  startDate: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  price: number;
  paymentMethodId: string;
}

interface BillingStore {
  paymentMethods: PaymentMethod[];
  invoices: Invoice[];
  subscription: Subscription | null;
  version: number;
  addPaymentMethod: (method: Omit<PaymentMethod, 'id'>) => string;
  removePaymentMethod: (id: string) => void;
  setDefaultPaymentMethod: (id: string) => void;
  addInvoice: (invoice: Omit<Invoice, 'id'>) => void;
  updateSubscription: (subscription: Subscription) => void;
  cancelSubscription: () => void;
  getPaymentMethodById: (id: string) => PaymentMethod | undefined;
  processServiceFee: (leaseRequestId: string, amount: number, userId: string) => Promise<boolean>;
  resetStore: () => void;
}

const initialState = {
  paymentMethods: [],
  invoices: [],
  subscription: null,
  version: 1
};

export const useBillingStore = create<BillingStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      addPaymentMethod: (method) => {
        const id = Date.now().toString();
        const newMethod = { ...method, id };
        
        set(state => ({
          paymentMethods: [
            ...state.paymentMethods.map(m => ({ ...m, isDefault: false })),
            { ...newMethod, isDefault: true }
          ]
        }));
        
        return id;
      },

      removePaymentMethod: (id) => {
        set(state => ({
          paymentMethods: state.paymentMethods.filter(m => m.id !== id)
        }));
      },

      setDefaultPaymentMethod: (id) => {
        set(state => ({
          paymentMethods: state.paymentMethods.map(method => ({
            ...method,
            isDefault: method.id === id
          }))
        }));
      },

      addInvoice: (invoice) => {
        const id = Date.now().toString();
        set(state => ({
          invoices: [...state.invoices, { ...invoice, id }]
        }));
      },

      updateSubscription: (subscription) => {
        set({ subscription });
      },

      cancelSubscription: () => {
        set(state => ({
          subscription: state.subscription
            ? { ...state.subscription, cancelAtPeriodEnd: true }
            : null
        }));
      },

      getPaymentMethodById: (id) => {
        return get().paymentMethods.find(method => method.id === id);
      },

      processServiceFee: async (leaseRequestId: string, amount: number, userId: string) => {
        const { paymentMethods } = get();
        const defaultPaymentMethod = paymentMethods.find(m => m.isDefault);

        if (!defaultPaymentMethod) {
          return false;
        }

        try {
          // In a real app, this would make an API call to process the payment
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Create invoice for the service fee
          const invoice: Omit<Invoice, 'id'> = {
            userId,
            amount,
            status: 'paid',
            date: new Date().toISOString(),
            description: 'Service fee for approved sublease',
            paymentMethodId: defaultPaymentMethod.id,
            type: 'service_fee',
            leaseRequestId
          };

          get().addInvoice(invoice);
          return true;
        } catch (error) {
          console.error('Failed to process service fee:', error);
          return false;
        }
      },

      resetStore: () => set(initialState)
    }),
    {
      name: 'billing-store',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Migration from version 0 to 1
          return {
            ...persistedState,
            version: 1,
            invoices: persistedState.invoices || [],
            paymentMethods: persistedState.paymentMethods || [],
            subscription: persistedState.subscription || null
          };
        }
        return persistedState;
      }
    }
  )
);